import { type KaToastColor, useToast } from '@/kasten-components';

import EventBus from './event-bus';

const MESSAGE_TYPE_TO_COLOR: Record<string, KaToastColor> = {
  info: 'primary',
  danger: 'error',
  warn: 'warning',
  success: 'success',
};

const MIN_DISPLAY_TIME = 3200; // ms

// close callbacks stored per toast id
const closeCallbacks = new Map<string | number, () => void>();

export interface FlashButton {
  text: string;
  icon?: string;
  clickFn?: () => void;
}

export interface FlashOptions {
  message: string;
  details?: string;
  messageType?: 'info' | 'danger' | 'warn' | 'success';
  icon?: string;
  dismiss?: 'auto' | 'user' | number;
  closable?: boolean;
  closeFn?: () => void;
  buttons?: FlashButton[] | readonly FlashButton[];
}

function stripHtml(html: string | undefined): string | undefined {
  if (!html) return html;
  return html.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '');
}

function computeDuration(options: FlashOptions): number {
  if (options.dismiss === 'user') return 0;
  if (typeof options.dismiss === 'number') return options.dismiss;

  const words = `${options.message} ${options.details ?? ''}`.split(' ').length;
  return Math.max(words * 500, MIN_DISPLAY_TIME);
}

function mapButtons(buttons?: FlashButton[] | readonly FlashButton[]) {
  if (!buttons?.length) return undefined;
  return buttons.map((btn) => ({
    label: btn.text,
    onClick: btn.clickFn,
  }));
}

const toast = useToast();

// Exposed for binding to KaToaster in App.vue
export const toasts = toast.toasts;

export function showFlash(options: FlashOptions = {} as FlashOptions) {
  const color = MESSAGE_TYPE_TO_COLOR[options.messageType ?? 'info'] ?? 'primary';
  const duration = computeDuration(options);

  const description = stripHtml(options.details);

  // Deduplicate: skip if an identical open toast already exists
  if (
    toast.toasts.value.some(
      (t) => t.open && t.title === options.message && t.description === description,
    )
  ) {
    return;
  }

  const item = toast.add({
    title: options.message,
    description,
    color,
    duration,
    closable: options.closable ?? true,
    showProgressBar: duration > 0,
    actions: mapButtons(options.buttons),
  });

  if (options.closeFn) {
    closeCallbacks.set(item.id, options.closeFn);
  }
}

export function removeToast(id: string | number) {
  toast.remove(id);
  const cb = closeCallbacks.get(id);
  if (cb) {
    cb();
    closeCallbacks.delete(id);
  }
}

export interface ModalOptions {
  show?: boolean;
  title?: string;
  message?: string;
  submitButtonText?: string;
  submitButtonType?: string;
  cancelButton?: boolean;
  backdrop?: boolean;
  onSubmit?: () => void;
  onClose?: () => void;
}

export interface EditorModalOptions {
  show?: boolean;
  backdrop?: boolean;
  title?: string;
  content?: string;
  lang?: string;
  error?: string;
}

export function showModal(options: ModalOptions = {}) {
  const opt = options;
  opt.show = true;
  opt.onSubmit = opt.onSubmit || function onSubmit() {};
  opt.onClose = opt.onClose || function onClose() {};
  EventBus.$emit('global-modal-show', opt);
}

export function showEditorModal(options: EditorModalOptions = {}) {
  const opt = options;
  opt.show = true;
  EventBus.$emit('global-editor-modal-show', opt);
}
